import React, { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ESRI } from '@rsos/maps/constants/mapProviders';
import {
  GISServiceTypes,
  GISStateLayer,
  selectGISLayers,
} from '@rsos/reduxDataStore';
import { selectMapProvider } from '@rsos/sinatra';
import { GISLayerControl } from './GISLayerControl';

/**
 * Check if a layer is ECC-specific (has 'geocomm' in the URL)
 */
const isEccSpecificLayer = (layer: GISStateLayer): boolean => {
  const url = layer.service.gis_service_url?.toLowerCase() || '';
  return url.includes('geocomm');
};

/**
 * Check if a layer is a general supplemental layer (NOT ECC-specific)
 * These are layers like traffic, weather from services9.arcgis.com, traffic.arcgis.com, etc.
 */
const isGeneralSupplementalLayer = (layer: GISStateLayer): boolean => {
  if (layer.service.service_type !== GISServiceTypes.supplemental) {
    return false;
  }
  // If it's ECC-specific, it's not a general supplemental layer
  return !isEccSpecificLayer(layer);
};

/**
 * Check if a layer is a WMS or WMTS layer
 */
const isWmsWmtsLayer = (layer: GISStateLayer): boolean => {
  return (
    layer.service.service_type === GISServiceTypes.wms ||
    layer.service.service_type === GISServiceTypes.wmts
  );
};

/**
 * Check if a layer should be excluded (basemap, geocode)
 */
const isExcludedLayer = (layer: GISStateLayer): boolean => {
  return (
    layer.service.service_type === GISServiceTypes.basemap ||
    layer.service.service_type === GISServiceTypes.geocode
  );
};

type LayerFilter = 'ecc-specific' | 'supplemental' | 'wms-wmts';

interface GISLayerControlsProps {
  /** Filter to only show specific layer types */
  filter?: LayerFilter;
}

/**
 * A list of checkboxes controlling GIS layers
 * Order in MapLayersControl:
 *   1. ECC-specific layers (geocomm URLs)
 *   2. General supplemental layers (traffic, weather, etc.)
 *   3. Static/Dynamic spatial layers (rendered separately)
 *   4. WMS/WMTS layers
 *
 * Basemap and geocode layers are always excluded.
 */
export const GISLayerControls = memo<GISLayerControlsProps>(({ filter }) => {
  const mapProvider = useSelector(selectMapProvider);
  const layers = useSelector(selectGISLayers);
  const tooltip = mapProvider === ESRI ? undefined : 'Available with Esri Maps';

  const filteredLayers = useMemo(() => {
    return layers.filter(layer => {
      // Always exclude basemap and geocode layers
      if (isExcludedLayer(layer)) {
        return false;
      }

      // Apply filter if specified
      if (filter === 'ecc-specific') {
        return isEccSpecificLayer(layer);
      }
      if (filter === 'supplemental') {
        return isGeneralSupplementalLayer(layer);
      }
      if (filter === 'wms-wmts') {
        return isWmsWmtsLayer(layer);
      }

      // No filter - show all non-excluded layers
      return true;
    });
  }, [layers, filter]);

  const layerControls = useMemo(
    () => filteredLayers.map(l => <GISLayerControl layer={l} key={l.id} />),
    [filteredLayers],
  );

  if (filteredLayers.length === 0) {
    return null;
  }

  return <div title={tooltip}>{layerControls}</div>;
});
